import {
  UNDATED_PERIOD_KEY,
  describePeriodKey,
  periodKeysOf,
  placeholderDisplayName,
} from '@dataroom/shared';
import type { CategoryNode, PeriodNode, PeriodNodeKind, RoomDocument } from '@dataroom/shared';

/** One folder in the buyer's tree: a category from the documents' paths, or a
 * period under the folder a document is filed in (from its date of data and
 * period tags): a year, a quarter or month inside it, or "Undated". */
export interface FolderNode {
  /** Stable, unique key (the full path, so equal names in different branches
   * never collide). */
  key: string;
  name: string;
  /** The name with its context for assistive tech ("January 2025"). */
  fullName: string;
  kind: 'folder' | PeriodNodeKind;
  children: FolderNode[];
  /** Documents filed directly here. */
  documents: RoomDocument[];
  /** Every document at or below this folder. */
  count: number;
}

export interface FolderTree {
  roots: FolderNode[];
  /** Documents with no category path (shown after the folders). */
  unfiled: RoomDocument[];
}

const SEP = '\u001f';
const byName = (a: { name?: string | null }, b: { name?: string | null }) =>
  String(a.name ?? '').localeCompare(String(b.name ?? ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  });

/**
 * Build the buyer's folder tree from the documents they can see — and ONLY
 * from those: a folder exists because a visible document is filed in (or
 * under) it, so an area the buyer has no grant for never appears, not even
 * locked or greyed. Each document's `categoryPath` gives its folders; its
 * `dataDate` and `period` give the last levels under the folder it is filed in,
 * as the admin data room shows them: Year → Month (monthly) or Year → Q1–Q4
 * (quarterly); an annual document sits in its year. In a folder that has year
 * levels, its undated documents go to an "Undated" level after the years;
 * elsewhere they sit in the folder itself. A template placeholder folder
 * ("[Account Name]") reads "Unassigned account". Folders sort by name
 * (numeric-aware, so "2. Financial" precedes "10. Legal"), then years newest
 * first, quarters and months in calendar order, documents by name.
 *
 * Rows are read defensively: a malformed row (no `categoryPath` array, no
 * name) is treated as unfiled rather than throwing.
 */
export function buildFolderTree(documents: readonly RoomDocument[]): FolderTree {
  const roots: FolderNode[] = [];
  const unfiled: RoomDocument[] = [];
  const index = new Map<string, FolderNode>();

  const child = (
    siblings: FolderNode[],
    key: string,
    name: string,
    kind: FolderNode['kind'],
    fullName: string = name,
  ) => {
    let node = index.get(key);
    if (!node) {
      node = { key, name, fullName, kind, children: [], documents: [], count: 0 };
      index.set(key, node);
      siblings.push(node);
    }
    return node;
  };

  for (const doc of documents) {
    const path = Array.isArray(doc.categoryPath)
      ? doc.categoryPath.filter((s) => typeof s === 'string' && s.trim() !== '')
      : [];
    if (path.length === 0) {
      unfiled.push(doc);
      continue;
    }
    let siblings = roots;
    let key = '';
    let node: FolderNode | null = null;
    for (const segment of path) {
      key = key ? `${key}${SEP}${segment}` : segment;
      const name = placeholderDisplayName(segment);
      node = child(siblings, key, name, 'folder');
      siblings = node.children;
    }
    for (const periodKey of periodKeysOf(doc)) {
      const period = periodKey === UNDATED_PERIOD_KEY ? null : describePeriodKey(periodKey);
      if (!node || !period) break;
      key = `${key}${SEP}#${periodKey}`;
      node = child(node.children, key, period.label, period.kind, period.fullLabel);
    }
    node?.documents.push(doc);
  }

  // A folder with year levels moves its undated documents to "Undated".
  const gatherUndated = (nodes: FolderNode[]) => {
    for (const n of nodes) {
      if (n.kind !== 'folder') continue;
      if (n.documents.length > 0 && n.children.some((c) => c.kind === 'year')) {
        const undated = child(
          n.children,
          `${n.key}${SEP}#${UNDATED_PERIOD_KEY}`,
          'Undated',
          'undated',
        );
        undated.documents.push(...n.documents);
        n.documents = [];
      }
      gatherUndated(n.children);
    }
  };
  gatherUndated(roots);

  const RANK: Record<FolderNode['kind'], number> = {
    // The admin's "Needs filing" row; this tree never builds one.
    needs_filing: -1,
    folder: 0,
    quarter: 1,
    month: 2,
    year: 3,
    undated: 4,
  };
  const finish = (nodes: FolderNode[]): number => {
    // Folders by name; then years newest first; quarters and months in
    // calendar order (their keys end in `Q1` / `2025-03`); Undated last.
    nodes.sort((a, b) =>
      a.kind !== b.kind
        ? RANK[a.kind] - RANK[b.kind]
        : a.kind === 'year'
          ? byName(b, a)
          : a.kind === 'folder'
            ? byName(a, b)
            : a.key.localeCompare(b.key),
    );
    let total = 0;
    for (const n of nodes) {
      n.documents.sort(byName);
      n.count = n.documents.length + finish(n.children);
      total += n.count;
    }
    return total;
  };
  finish(roots);
  unfiled.sort(byName);
  return { roots, unfiled };
}

/** The buyer's tree in the shape the shared folder column draws
 * (`DataRoomFolders`): folders as category nodes (id = the folder's key), and
 * under each folder its Year → Quarter / Month (and Undated) levels as period
 * nodes keyed by their period key (`y:2025`, `q:2025-Q1`, `undated`). */
export interface FolderColumnView {
  nodes: CategoryNode[];
  periodNodes: Map<string, PeriodNode[]>;
}

/** The period key a period node of the tree stands for (`y:2025`). */
const periodKeyOf = (node: FolderNode): string =>
  node.key.slice(node.key.lastIndexOf(`${SEP}#`) + 2);

export function folderColumnView(tree: FolderTree): FolderColumnView {
  const periodNodes = new Map<string, PeriodNode[]>();
  const period = (node: FolderNode): PeriodNode => ({
    key: periodKeyOf(node),
    kind: node.kind as PeriodNodeKind,
    label: node.name,
    fullLabel: node.fullName,
    count: node.count,
    children: node.children.map(period),
  });
  const folder = (node: FolderNode): CategoryNode => {
    const periods = node.children.filter((c) => c.kind !== 'folder');
    if (periods.length > 0) periodNodes.set(node.key, periods.map(period));
    return {
      id: node.key,
      name: node.name,
      documentCount: node.count,
      children: node.children.filter((c) => c.kind === 'folder').map(folder),
    };
  };
  return { nodes: tree.roots.map(folder), periodNodes };
}

function findFolder(nodes: readonly FolderNode[], key: string): FolderNode | null {
  for (const n of nodes) {
    if (n.key === key) return n;
    const below = findFolder(n.children, key);
    if (below) return below;
  }
  return null;
}

function documentsBelow(node: FolderNode): RoomDocument[] {
  return [...node.documents, ...node.children.flatMap(documentsBelow)];
}

/**
 * What a pick in the folder column shows: every document at or below the
 * folder (or, with a period key, at or below that period level of it); all
 * the documents for no folder. Null for a folder or period no longer in the
 * tree.
 */
export function documentsInFolder(
  tree: FolderTree,
  documents: readonly RoomDocument[],
  folderKey: string | null,
  periodKey: string | null,
): RoomDocument[] | null {
  if (folderKey === null) return [...documents];
  const folder = findFolder(tree.roots, folderKey);
  if (!folder) return null;
  if (periodKey === null) return documentsBelow(folder);
  const inPeriods = (nodes: readonly FolderNode[]): FolderNode | null => {
    for (const n of nodes) {
      if (n.kind === 'folder') continue;
      if (periodKeyOf(n) === periodKey) return n;
      const below = inPeriods(n.children);
      if (below) return below;
    }
    return null;
  };
  const node = inPeriods(folder.children);
  return node ? documentsBelow(node) : null;
}

/** The folder names down to `folderKey` (["Financial", "Bank Statements"]). */
export function folderPathNames(tree: FolderTree, folderKey: string): string[] {
  const walk = (nodes: readonly FolderNode[]): string[] => {
    for (const n of nodes) {
      if (n.kind !== 'folder') continue;
      if (n.key === folderKey) return [n.name];
      const below = walk(n.children);
      if (below.length > 0) return [n.name, ...below];
    }
    return [];
  };
  return walk(tree.roots);
}
